import React, { useState } from "react";
import { useHistory } from "react-router";
import _ from "lodash";
import clsx from "clsx";
import { Collapse, IconButton, Typography, Button } from "@material-ui/core";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { NeedIcon, NeedListItem, AcuityNeedItem, PriorityBadge } from "./TransferIcons";
import { getRequestedStatusString } from "./helpers/transferScheduleHelpers";
import { getOriginLocationString, getDestinationLocationString } from "../../common/helpers";
import { makeStyles } from "@material-ui/core/styles";
import { consts } from "../../common/consts";
import moment from "moment";

const timeToAgoString = (date: any): string => {
  const timenow = moment();
  const diffMin = Math.floor(moment.duration(timenow.diff(moment(date))).asMinutes());
  if (diffMin <= 0) {
    return "Just Now";
  } else {
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays > 0 ? diffDays + "d " : ""}${diffHours > 0 ? (diffHours % 20) + "h " : ""}${diffMin % 60 > 0 ? (diffMin % 60) + "m" : ""} ago`;
  }
};

const TransferScheduleAdHocTable = ({
  transfers,
  updatedTransfers,
  setCurrentTransferId,
  setCurrentTransferData,
  setCurrentBookingId,
  setShowScheduleModal,
  setShowScheduleModalAssignVehicleOption,
  roles,
}: any) => {
  const classes = useStyles();
  const history = useHistory();

  const [titleCollapsed, setTitleCollapsed] = useState(false);

  return (
    <div className={classes.root}>
      <div className={classes.table}>
        <div className={classes.title}>
          <IconButton
            style={{ marginLeft: "-10px" }}
            aria-label="expand row"
            size="small"
            onClick={() => setTitleCollapsed(!titleCollapsed)}
            className={classes.removeHover}
            disableRipple
          >
            {!titleCollapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            <Typography variant="body1" component="div" className={classes.rowTitle}>
              Ad-hoc Requested
            </Typography>
            {titleCollapsed && (
              <Typography
                variant="body1"
                component="div"
                style={{
                  fontSize: 21,
                  fontWeight: 500,
                  opacity: 0.36,
                  marginLeft: 15,
                }}
              >
                {transfers.length} Transfers
              </Typography>
            )}

            {!titleCollapsed && (
              <Typography
                variant="body1"
                component="div"
                style={{
                  fontSize: 16,
                  fontWeight: 400,
                  opacity: 0.36,
                  marginLeft: 15,
                }}
              >
                Client is requesting SMA to schedule the following transfers
              </Typography>
            )}
          </IconButton>
        </div>

        <Collapse in={!titleCollapsed} timeout="auto" unmountOnExit>
          <div className={classes.tableHead}>
            <div className={classes.submittedColumn}>
              <span>Submitted</span>
            </div>
            <div className={classes.patientColumn}>Patient</div>
            <div className={classes.needsColumn}>Needs</div>
            <div className={classes.fromColumn}>From</div>
            <div className={classes.toColumn}>To</div>
            <div className={classes.priorityColumn}>Pri</div>
            <div className={classes.statusColumn}>Status</div>
            <div className={classes.actionButtonColumn}></div>
          </div>

          {transfers.map((row: any, index: number) => {
            return (
              <div
                className={clsx(classes.tableRow, {
                  [classes.animatedUpdatedItem]:
                    updatedTransfers &&
                    updatedTransfers.some((e: any) => e.TransferId === row.transferId && new Date().getTime() - e.UpdatedDate < 60000),
                })}
                key={index}
                onClick={(e) => history.push(`/transferDetails/${row.bookingId}/${row.transferId}`)}
              >
                <div className={classes.submittedColumn}>
                  <span>{timeToAgoString(row.createdOn)}</span>
                </div>
                <div className={classes.patientColumn}>
                  {row.firstName[0]} {row.lastName}
                </div>
                <div className={classes.needsColumn}>
                  {row.bookingSpecialNeeds.map((item: any) => (
                    <NeedIcon
                      key={row.bookingId + item.specialNeedId}
                      iconName={item.specialNeedIconUrl.split("/")[item.specialNeedIconUrl.split("/").length - 1]}
                    />
                  ))}
                  {row.mobility === 1 && <NeedListItem key="notambulant" text={"Not Ambulant"} iconUrl={"../../assets/svgs/nonambulant.svg"} />}
                  {row.mobility !== 1 && <NeedListItem key="ambulant" text={"Ambulant"} iconUrl={"../../assets/svgs/ambulant.svg"} />}
                  {row.classification && <AcuityNeedItem key="acuity" text={row.classification} />}
                </div>
                <div className={classes.fromColumn}>{getOriginLocationString(row, true)}</div>
                <div className={classes.toColumn}>{getDestinationLocationString(row)}</div>

                <div className={classes.priorityColumn}>
                  <PriorityBadge priority={row.priority} />
                </div>
                <div className={classes.statusColumn}>
                  <div className={classes.requestText}>{getRequestedStatusString(row)}</div>
                  {(row.canBeScheduledByCurrentUser || (roles.isSMADriver && row.status === consts.TransferStatus.AdhocRequest)) && (
                    <Button style={{ minWidth: 0, padding: 0 }}>
                      <span
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setCurrentTransferId(row.transferId);
                          setCurrentTransferData(row);
                          setCurrentBookingId(row.bookingId);
                          setShowScheduleModalAssignVehicleOption(true);
                          setShowScheduleModal(true);
                        }}
                        className={classes.yellowBoxText}
                      >
                        Schedule
                      </span>
                    </Button>
                  )}
                </div>

                <div className={classes.actionButtonColumn}>
                  <span>
                    <IconButton aria-label="expand row" size="small">
                      <ArrowForwardIosIcon style={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </div>
              </div>
            );
          })}
          {transfers.length === 0 && <div className={classes.noTransfersText}>No Records Found</div>}
        </Collapse>
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  root: {},
  table: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    paddingBottom: 10,
  },
  tableHead: {
    height: 43,
    minHeight: 43,
    maxHeight: 43,
    fontWeight: 400,
    fontSize: "18px",
    lineHeight: "22px",
    color: "rgba(255, 255, 255, 0.3)",
    display: "flex",
    alignItems: "center",
    borderTop: "1px solid #00152E",
    // marginLeft: 20,
    // marginRight: 16,
  },
  tableRow: {
    display: "flex",
    alignItems: "center",
    color: "#FFFFFF",
    fontWeight: 500,
    backgroundColor: "rgb(2, 72, 157, 0.23)",
    height: 45,
    minHeight: 45,
    maxHeight: 45,
    // paddingLeft: 20,
    // paddingRight: 16,
    fontSize: "18px",
    lineHeight: "22px",
    borderTop: "1px solid #00152E",
    cursor: "pointer",

    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },

    "&:not(:last-child)": {
      borderBottom: "1px solid #00152E",
    },
  },
  tableCell: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    height: 33,
  },
  animatedUpdatedItem: {
    animation: `$updatedTransferEffect 60000ms ease-out`,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08) !important",
    },
  },
  "@keyframes updatedTransferEffect": {
    "0%": { backgroundColor: "#F4B8273A" },
    "100%": { backgroundColor: "#002047" },
  },
  submittedColumn: {
    flex: "0 0 10%",
    display: "flex",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",

    "&>span": {
      paddingLeft: 20,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  patientColumn: {
    flex: "0 0 11%",
    display: "flex",

    "& span": {
      color: "#010B18",
      opacity: "0.39",
    },

    "@media (max-width: 1280px)": {
      display: "none",
    },
  },
  needsColumn: {
    flex: "0 0 10%",
    display: "flex",
    alignItems: "center",

    "& img": {
      width: 18,
      height: 18,
    },
    "@media (max-width: 1280px)": {
      display: "none",
    },
  },
  fromColumn: {
    flex: "0 0 20%",
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",

    "@media (max-width: 1280px)": {
      flex: "0 0 26%",
    },
  },
  toColumn: {
    flex: "0 0 20%",
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",

    "@media (max-width: 1280px)": {
      flex: "0 0 26%",
    },
  },
  priorityColumn: {
    flex: "0 0 5%",
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  statusColumn: {
    flex: "0 0 18%",
    overflow: "hidden",

    "@media (max-width: 1280px)": {
      flex: "0 0 27%",
    },
  },
  actionButtonColumn: {
    justifyContent: "flex-end",
    flex: "0 0 6%",
    display: "flex",

    "&>span": {
      paddingRight: 16,
    },
  },
  noTransfersText: {
    display: "flex",
    justifyContent: "center",
    fontWeight: 500,
    height: 55,
    fontSize: "18px",
    lineHeight: "22px",
    alignItems: "center",
  },
  checkboxes: {
    padding: 0,
    margin: 0,
  },
  requestText: {
    marginRight: 10,
    paddingTop: 5,
    float: "left",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "50%",
  },
  yellowBoxText: {
    background: "#F4B827",
    padding: "4px 14px",
    fontSize: 14,
    fontWeight: 700,
    color: "#03090E",
    borderRadius: 4,
    cursor: "pointer",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  groupStart: {
    backgroundColor: "transparent",
    width: "10px",
    height: "20px",
    borderTop: "2px solid #fff",
    borderLeft: "2px solid #fff",
    alignSelf: "flex-end",
  },
  groupMiddle: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  groupMidTop: {
    backgroundColor: "transparent",
    width: "10px",
    height: "21px",
    borderLeft: "2px solid #fff",
  },
  groupMidBottom: {
    backgroundColor: "transparent",
    width: "10px",
    height: "21px",
    borderTop: "2px solid #fff",
    borderLeft: "2px solid #fff",
  },
  groupEnd: {
    backgroundColor: "transparent",
    width: "10px",
    height: "20px",
    borderBottom: "2px solid #fff",
    borderLeft: "2px solid #fff",
    alignSelf: "flex-start",
  },
  removeHover: {
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  rowTitle: {
    fontSize: 21,
    fontWeight: 400,
    marginLeft: 15,
  },
  title: {
    display: "flex",
    paddingLeft: 20,
  },
});

export default TransferScheduleAdHocTable;
